import * as React from "react";

import {
  IContextualMenuItem,
  IContextualMenuRenderItem,
} from "@fluentui/react/lib/ContextualMenu";

import {
  dataUniqueId,
  getInputMethodFromEvent,
  InputMethod,
} from "@1js/acui-common";
import { iconNameToIconProps } from "@1js/acui-icons";
import { itemInMenuOnExecute } from "@1js/acui-keytip";
import { memoizeLastResultPerId } from "@1js/acui-memoization";
import {
  deriveMenuKeytip,
  MenuItemRenderComponent,
  sanitizeLabel,
} from "@1js/acui-menu";

import {
  ControlInMenuStyleFnOrObject,
  getClassNamesForMenuButton,
} from "../AppBaseButton";
import { AppButtonProps } from "./AppButton.Props";

export const getAppButtonMenuItem = memoizeLastResultPerId(
  (
    _idAndLocation: string,
    controlProps: AppButtonProps,
    customMenuItemStyles?: ControlInMenuStyleFnOrObject,
    customProps?: IContextualMenuItem,
    menuItemRenderer?: MenuItemRenderComponent
  ): IContextualMenuItem => {
    // It is possible in the ContextualMenu to pass in a event that could be a MouseEvent or a KeyboardEvent
    const onExecute = (
      ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
    ): void => {
      controlProps.onExecute &&
        controlProps.onExecute({
          id: controlProps.id,
          location: controlProps.location,
          inputMethod: ev && getInputMethodFromEvent(ev),
          shouldTakeFocus: controlProps.shouldTakeFocus,
          triggerTime: ev && ev.timeStamp,
        });

      if (controlProps.dismissHostingSurfaceOnExecute === false) {
        // preventDefault will cause the menu to not close
        // https://developer.microsoft.com/en-us/fluentui#/controls/web/contextualmenu#IContextualMenuItem
        ev?.preventDefault();
      }
    };
    const onKeytipExecute = (): void =>
      controlProps.onExecute &&
      controlProps.onExecute({
        id: controlProps.id,
        location: controlProps.location,
        inputMethod: InputMethod.Keyboard,
        shouldTakeFocus: controlProps.shouldTakeFocus,
      });
    const contextualItem = React.createRef<IContextualMenuRenderItem>();
    return {
      ...customProps,
      key: controlProps.id,
      name: controlProps.label,
      iconProps: iconNameToIconProps(controlProps.icon, controlProps.iconColor),
      onClick: onExecute,
      onFocus: controlProps.onFocus,
      onBlur: controlProps.onBlur,
      disabled: controlProps.disabled,
      onRenderContent: menuItemRenderer,
      getItemClassNames: getClassNamesForMenuButton(customMenuItemStyles),
      keytipProps:
        controlProps.keytipProps &&
        deriveMenuKeytip(
          controlProps.keytipProps,
          (executeTarget: HTMLElement) => {
            itemInMenuOnExecute(
              executeTarget,
              contextualItem,
              onKeytipExecute,
              controlProps.dismissHostingSurfaceOnExecute
            );
          }
        ),
      componentRef: contextualItem,
      lang: controlProps.lang,
      title: undefined,
      ariaLabel: sanitizeLabel(
        controlProps.fullDescriptionLabel || controlProps.label
      ),
      ariaDescription: controlProps.customTooltip,
      secondaryText: controlProps.secondaryText,
      "data-unique-id": dataUniqueId(controlProps),
      "aria-keyshortcuts": controlProps.keyShortcuts,
    };
  }
);
